/* eslint-disable max-statements */
import { Injectable, OnDestroy } from '@angular/core'
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { BehaviorSubject, Subscription } from 'rxjs'
import { AEMChunk } from 'src/app/services/AEM/aem-chunk.model'
import { CentralContentService } from 'src/app/services/content/central-content.service'

import { DataFilters } from '../models/DataFilters.model'
import {
  ASSET_CLASS_FILTERS,
  CREDIT_QUALITY_FILTERS,
  INVESTMENT_MINIMUM_FILTERS,
  MANAGEMENT_STYLE_FILTERS,
  MARKET_CAPITALIZATION_FILTERS,
  MATURITY_FILTERS, PRODUCT_TYPE,
  REGION_FILTERS,
  RISK_FILTERS,
  SHARE_CLASS_FILTERS,
  STRATEGY_FILTERS,
  STRATEGY_FILTERS_529,
  STYLE_FILTERS,
  TAX_EFFICIENCY_FILTERS,
} from '../models/filter-details.model'
import { FundDetails } from '../models/fund-details.model'
import { FundDetailsService } from './fund-details.service'

const NEGATIVE_ONE = -1
const ONE = 1
const ONES = '1'
const TWO = '2'
const THREE = '3'
const FOUR = '4'
const FIVE = '5'
const SIX = '6'
const SEVEN = '7'
const EIGHT = '8'
const NINE = '9'
const ONE_THOUSAND = 1000
const THREE_THOUSAND = 3000
const TEN_THOUSAND = 10000
const TWENTY_FIVE_THOUSAND = 25000
const FIFTY_THOUSAND = 50000
const STRATEGY_FUND = '1298'
const NON_VANGUARD_MF= 'non-vanguard-mutual-funds'
const MUTUAL_FUNDS = 'mutual-funds'

@Injectable({
  providedIn: 'root',
})
export class FiltersService implements OnDestroy {
  private readonly _filteredDataSource = new BehaviorSubject<FundDetails>(null)
  public filteredData$ = this._filteredDataSource.asObservable()

  private withoutAssetClassFilters = false
  private readonly _withoutAssetClassFilteredDataSource = new BehaviorSubject<
    FundDetails
  >(null)
  public withoutAsetClassFilteredData$ = this._withoutAssetClassFilteredDataSource.asObservable()
  public activeFilters: DataFilters

  public numAppliedFilters: number
  public compareFundCount = 0
  public compareFunds = new BehaviorSubject([])
  public FundFamilyId;
  openFilters = false;
  // AEM content Vars
  ESGFundIds: AEMChunk
  CorePortfolio: AEMChunk
  TargetEnrollmentFundIds: AEMChunk
  cashFundTickers: AEMChunk
  aemContentSub: Subscription
  

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly fundDetailsService: FundDetailsService,
    private readonly centralContentService: CentralContentService,
  ) {
    this.initializeFilterObject()
    this.checkParameters()
  }

  public readonly discoverByMap = {
    all: '',
    etfs: 'isETF',
    'mutual-funds': 'isMutualFund',
    '529-portfolios': 'is529',
    'non-vanguard-mutual-funds': ''
  }

  parseAEMContent(content) {
    this.ESGFundIds = this.centralContentService.getContentByBody(
      'configuration_strategyfilter_ESG',
      content,
    )
    this.CorePortfolio = this.centralContentService.getContentByBody(
      'configuration_strategyfilter_totalmarketETF',
      content,
    )
    this.TargetEnrollmentFundIds = this.centralContentService.getContentByBody(
      'configuration_strategyfilter_all_in_one',
      content,
    )
    this.cashFundTickers = this.centralContentService.getContentByBody(
      'configuration_strategyfilters_cash',
      content
    )
  }

  applyFilters() {
    const conditions = []
    let params = {}
    this.numAppliedFilters = 0
    this.createDiscoverByCondition(conditions)
    this.createStrategyCondition(conditions, params)
    this.createStrategyCondition529s(conditions,params)
    this.createManagementStyleCondition(conditions, params)
    this.createAssetClassCondition(conditions, params)
    this.createRiskCondition(conditions, params)
    this.createRegionCondition(conditions, params);
    this.createInvestmentMinimumCondition(conditions, params)
    this.createShareClassCondition(conditions, params)
    this.createTaxEfficiencyCondition(conditions, params)
    this.createToggleFilterCondition(params);
    this.createFundFamilyCondition(params);
    this.updateProductTypeFilterCount();
    this.activatedRoute.queryParams.subscribe((param: { cmpgn: any }) => {
      if (param.cmpgn !== undefined) {
        params['cmpgn'] = param.cmpgn
      }
    })

    if (conditions.length === 0) {
      this._filteredDataSource.next(
        (this.activeFilters.discoverBy !== NON_VANGUARD_MF ? this.fundDetailsService.fundDetails$ : this.fundDetailsService.fundFamilyDetails$).getValue(),
      )
      this._withoutAssetClassFilteredDataSource.next(
        (this.activeFilters.discoverBy !== NON_VANGUARD_MF ? this.fundDetailsService.fundDetails$ : this.fundDetailsService.fundFamilyDetails$).getValue(),
      )
    } else {
      this.withoutAssetClassFilters = false
      const newEntity = (this.activeFilters.discoverBy !== NON_VANGUARD_MF ? this.fundDetailsService.fundDetails$ : this.fundDetailsService.fundFamilyDetails$)
        .getValue()
        ?.fund?.entity?.filter((fund) => conditions.every((c) => c(fund)))
      const newFundObj = {
        ...(this.activeFilters.discoverBy !== NON_VANGUARD_MF ? this.fundDetailsService.fundDetails$ : this.fundDetailsService.fundFamilyDetails$).getValue(),
        size: newEntity ? newEntity.length : 0,
        fund: { entity: newEntity },
      }
      this._filteredDataSource.next(newFundObj)

      // without assetclass filter data using to show counts on assetclassfilters
      this.withoutAssetClassFilters = true
      const newEntity2 = (this.activeFilters.discoverBy !== NON_VANGUARD_MF ? this.fundDetailsService.fundDetails$ : this.fundDetailsService.fundFamilyDetails$)
        .getValue()
        ?.fund?.entity?.filter((fund) => conditions.every((c) => c(fund)))
      const newFundObj2 = {
        ...(this.activeFilters.discoverBy !== NON_VANGUARD_MF ? this.fundDetailsService.fundDetails$ : this.fundDetailsService.fundFamilyDetails$).getValue(),
        size: newEntity2 ? newEntity2.length : 0,
        fund: { entity: newEntity2 },
      }
      this._withoutAssetClassFilteredDataSource.next(newFundObj2)
    }
    this.router.navigate([this.activeFilters.discoverBy], {
      relativeTo: this.activatedRoute,
      queryParams: params,
      replaceUrl: true,
    })
  }

  clearFilters() {
    this.activeFilters.fundFamily = {}
    this.activeFilters.risk = []
    ASSET_CLASS_FILTERS.forEach((filter) => {
      this.activeFilters.assetClass.controls[filter].setValue(false)
      if (filter === 'equity') {
        MARKET_CAPITALIZATION_FILTERS.forEach((market) => {
          this.activeFilters.assetClassMarketCap[filter].controls[
            market
          ].setValue(false)
        })
        STYLE_FILTERS.forEach((style) => {
          this.activeFilters.assetClassStyle[filter].controls[style].setValue(
            false,
          )
        })
      }
      if (filter === 'fixed_income') {
        CREDIT_QUALITY_FILTERS.forEach((credit) => {
          this.activeFilters.assetClassCredit[filter].controls[credit].setValue(
            false,
          )
        })
        MATURITY_FILTERS.forEach((maturity) => {
          this.activeFilters.assetClassMaturity[filter].controls[
            maturity
          ].setValue(false)
        })
      }
    })
    SHARE_CLASS_FILTERS.forEach((filter) => {
      this.activeFilters.shareClass.controls[filter].setValue(false)
    })
    MANAGEMENT_STYLE_FILTERS.forEach((filter) => {
      this.activeFilters.managementStyle.controls[filter].setValue(false)
    })
    INVESTMENT_MINIMUM_FILTERS.forEach((filter) => {
      this.activeFilters.investmentMinimums.controls[filter].setValue(false)
    })
    TAX_EFFICIENCY_FILTERS.forEach((filter) => {
      this.activeFilters.taxEfficiency.controls[filter].setValue(false)
    })
    STRATEGY_FILTERS.forEach((filter) => {
      this.activeFilters.strategy.controls[filter].setValue(false)
    })
    STRATEGY_FILTERS_529.forEach((filter) => {
      this.activeFilters.strategy529.controls[filter].setValue(false)
    })
    this.activeFilters.productType.patchValue({'mutual-funds': false, 'etfs': false});
    REGION_FILTERS.forEach((filter) => {
      this.activeFilters.region.controls[filter].setValue(false)
    })
    this.activeFilters.discoverBy = 'all'
    this.applyFilters()
  }

  private initializeFilterObject() {
    this.activeFilters = {
      risk: [],
      assetClass: this.createFormGroup(ASSET_CLASS_FILTERS),
      productType: this.createFormGroup(PRODUCT_TYPE),
      assetClassMarketCap: this.createFormGroupArray(
        'marketCap',
        MARKET_CAPITALIZATION_FILTERS,
        ASSET_CLASS_FILTERS,
      ),
      assetClassStyle: this.createFormGroupArray(
        'style',
        STYLE_FILTERS,
        ASSET_CLASS_FILTERS,
      ),
      assetClassCredit: this.createFormGroupArray(
        'creditquality',
        CREDIT_QUALITY_FILTERS,
        ASSET_CLASS_FILTERS,
      ),
      assetClassMaturity: this.createFormGroupArray(
        'maturity',
        MATURITY_FILTERS,
        ASSET_CLASS_FILTERS,
      ),
      discoverBy: 'all',
      managementStyle: this.createFormGroup(MANAGEMENT_STYLE_FILTERS),
      shareClass: this.createFormGroup(SHARE_CLASS_FILTERS),
      investmentMinimums: this.createFormGroup(INVESTMENT_MINIMUM_FILTERS),
      taxEfficiency: this.createFormGroup(TAX_EFFICIENCY_FILTERS),
      strategy: this.createFormGroup(STRATEGY_FILTERS),
      region: this.createFormGroup(REGION_FILTERS),
      strategy529: this.createFormGroup(STRATEGY_FILTERS_529),
      fundFamily: {}
    }
    
    this.activeFilters.productType.valueChanges.subscribe((value) => {
      sessionStorage.setItem('mutual-funds', value['mutual-funds']);
      sessionStorage.setItem('etfs', value['etfs']);
    })
  }

  private createFormGroupArray(type: string, array: string[], loop: string[]) {
    const group = []

    loop.forEach((value) => {
      if (type === 'marketCap' || type === 'style') {
        if (value === 'equity') {
          group[value] = this.createFormGroup(array)
        }
      } else if (type === 'creditquality' || type === 'maturity') {
        if (value === 'fixed_income') {
          group[value] = this.createFormGroup(array)
        }
      } else {
        group[value] = this.createFormGroup(array)
      }
    })
    return group
  }

  private createFormGroup(array: string[]) {
    const group = {}
    array.forEach((value) => {
      group[value] = new UntypedFormControl(false)
    })
    return new UntypedFormGroup(group)
  }

  private checkParameters() {
    // eslint-disable-next-line complexity
    this.activatedRoute.queryParams.subscribe((params) => {
      const isNonVanguardMF = this.router.url.includes(NON_VANGUARD_MF); 
      const hasFundFamilyId = !!(params.FundFamilyId && !isNaN(params.FundFamilyId));

      if (!hasFundFamilyId && isNonVanguardMF) {
        this.router.navigate([MUTUAL_FUNDS], { queryParamsHandling: 'merge' });
      }

      if(hasFundFamilyId){
        this.FundFamilyId=params.FundFamilyId;
      }
      
      if(params.filters==="open"){
        this.openFilters=true
      }else{
        this.openFilters=false;
      }
      
      if (params.riskfilter !== undefined) {
        this.activeFilters.risk = params.riskfilter
          .split(',')
          .filter((value) => RISK_FILTERS.indexOf(value) > NEGATIVE_ONE)
      }
      if (params.assetclass) {
        params.assetclass
          .split(',')
          .filter(
            (value) =>
              ASSET_CLASS_FILTERS.indexOf(value.split('-')[0]) > NEGATIVE_ONE,
          )
          // eslint-disable-next-line complexity
          .map((value) => {
            this.checkAssetClassParams(value)
          })
      }
      if (params.shareclass) {
        params.shareclass
          .split(',')
          .filter((value) => SHARE_CLASS_FILTERS.indexOf(value) > NEGATIVE_ONE)
          .map((value) =>
            this.activeFilters.shareClass.controls[value].setValue(true),
          )
      }
      if (params.managementstyle) {
        params.managementstyle
          .split(',')
          .filter(
            (value) => MANAGEMENT_STYLE_FILTERS.indexOf(value) > NEGATIVE_ONE,
          )
          .map((value) =>
            this.activeFilters.managementStyle.controls[value].setValue(true),
          )
      }
      if (params.investmentminimumclass) {
        params.investmentminimumclass
          .split(',')
          .filter(
            (value) => INVESTMENT_MINIMUM_FILTERS.indexOf(value) > NEGATIVE_ONE,
          )
          .map((value) =>
            this.activeFilters.investmentMinimums.controls[value].setValue(
              true,
            ),
          )
      }
      if (params.strategy) {
        params.strategy
          .split(',')
          .filter((value) => STRATEGY_FILTERS.indexOf(value) > NEGATIVE_ONE)
          .map((value) => this.activeFilters.strategy.controls[value].setValue(true))
      }
      if (params.strategy529){
        params.strategy529
        .split(',')
          .filter((value) => STRATEGY_FILTERS_529.indexOf(value) > NEGATIVE_ONE)
          .map((value) => this.activeFilters.strategy529.controls[value].setValue(true))
      }
      if (params.taxefficiency) {
        this.checkTaxEfficiencyParams(params.taxefficiency)
      }
      if (params.region) {
        params.region
          .split(',')
          .filter(
            (value) => REGION_FILTERS.indexOf(value) > NEGATIVE_ONE,
          )
          .map((value) =>
            this.activeFilters.region.controls[value].setValue(true),
          )
      }
    })
  }

  checkTaxEfficiencyParams(taxValue) {
    taxValue
      .split(',')
      .filter((value) => TAX_EFFICIENCY_FILTERS.indexOf(value) > NEGATIVE_ONE)
      .map((value) =>
        this.activeFilters.taxEfficiency.controls[value].setValue(true),
      )
  }

  checkAssetClassParams(value) {
    const assetClass = value.split('-')[0]
    const marketCaps = value.split('-market_cap-')[1]
    const style = value.split('-style-')[1]
    const credit = value.split('-credit_quality-')[1]
    const maturity = value.split('-maturity-')[1]
    if (ASSET_CLASS_FILTERS.indexOf(assetClass) > NEGATIVE_ONE) {
      this.activeFilters.assetClass.controls[assetClass].setValue(true)
    }
    if (
      marketCaps &&
      MARKET_CAPITALIZATION_FILTERS.indexOf(marketCaps) > NEGATIVE_ONE
    ) {
      this.activeFilters.assetClassMarketCap[assetClass].controls[
        marketCaps
      ].setValue(true)
    }
    if (style && STYLE_FILTERS.indexOf(style) > NEGATIVE_ONE) {
      this.activeFilters.assetClassStyle[assetClass].controls[style].setValue(
        true,
      )
    }
    this.checkCreditMaturityParam(credit, maturity, assetClass)
  }

  checkCreditMaturityParam(credit, maturity, assetClass) {
    if (credit && CREDIT_QUALITY_FILTERS.indexOf(credit) > NEGATIVE_ONE) {
      this.activeFilters.assetClassCredit[assetClass].controls[credit].setValue(
        true,
      )
    }
    if (maturity && MATURITY_FILTERS.indexOf(maturity) > NEGATIVE_ONE) {
      this.activeFilters.assetClassMaturity[assetClass].controls[
        maturity
      ].setValue(true)
    }
  }

  updateProductTypeFilterCount() {
    const mutualFundsState = sessionStorage.getItem('mutual-funds') === 'true';
    const etfsState = sessionStorage.getItem('etfs') === 'true';

    if (mutualFundsState || etfsState) {
      this.activeFilters.productType.patchValue({ 'mutual-funds': mutualFundsState, 'etfs': etfsState }, { emitEvent: false });
    } else {
      this.activeFilters.productType.patchValue({ 'mutual-funds': false, 'etfs': false }, { emitEvent: false });
    }
    
    const activeProductTypeFilters = PRODUCT_TYPE.filter(
      (value) => this.activeFilters.productType.controls[value].value
    ).length;
    
    if (activeProductTypeFilters > 0) {
      this.numAppliedFilters += activeProductTypeFilters;
    }
  }

  createDiscoverByCondition(conditions) {   
    if (this.activeFilters.discoverBy !== 'all' && this.activeFilters.discoverBy !== NON_VANGUARD_MF) {
      conditions.push(
        (fund) =>
          fund.profile[this.discoverByMap[this.activeFilters.discoverBy]] ===
          true,
      )
    } else if (this.activeFilters.discoverBy === 'all') {
      conditions.push(
        (fund) =>
          fund.profile['isETF'] === true ||
          fund.profile['isMutualFund'] === true,
      )
    }
  }

  createRiskCondition(conditions, params) {
    if (this.activeFilters.risk.length > 0 && this.activeFilters.discoverBy !== NON_VANGUARD_MF) {
      this.numAppliedFilters += this.activeFilters.risk.length
      const filterParams = [...this.activeFilters.risk].sort()
      params['riskfilter'] = filterParams.toString()
      conditions.push(
        (fund) =>
          this.activeFilters.risk.indexOf(fund.risk && fund.risk.code && fund.risk.code.toString()) !==
          NEGATIVE_ONE,
      )
    }
  }

  createAssetClassCondition(conditions, params) {
    if (
      ASSET_CLASS_FILTERS.filter(
        (value) => this.activeFilters.assetClass.controls[value].value,
      ).length > 0
    ) {
      this.numAppliedFilters += ASSET_CLASS_FILTERS.filter(
        (value) => this.activeFilters.assetClass.controls[value].value,
      ).length
      params['assetclass'] = this.concatParams(
        ASSET_CLASS_FILTERS.filter(
          (value) => this.activeFilters.assetClass.controls[value].value,
        ).sort(),
      ).toString()

      conditions.push(
        // withoutAssetClassFilters true it won't look for other filters
        // eslint-disable-next-line complexity
        (fund) =>
          this.withoutAssetClassFilters ||
          (this.activeFilters.assetClass.controls['equity'].value &&
            fund.profile.fundFact.isStock === true &&
            fund.profile.fundFact.isSectorSpecific === false &&
            fund.profile.fundFact.isSpecialtyStockFund === false &&
            fund.profile.fundId !== STRATEGY_FUND &&
            fund.profile?.category !== 'Long-Short' &&
            this.filterByMarketCap('equity', fund) &&
            this.filterByStyle('equity', fund)) ||
          (this.activeFilters.assetClass.controls['fixed_income'].value &&
            fund.profile.fundFact.isBond === true &&
            this.filterByCredit('fixed_income', fund) &&
            this.filterByMaturity('fixed_income', fund)) ||
          (this.activeFilters.assetClass.controls['money_market'].value &&
            fund.profile.fundFact.isMoneyMarket === true) ||
          (this.activeFilters.assetClass.controls['multi_asset'].value &&
            fund.profile.fundFact.isBalanced === true) ||
          (this.activeFilters.assetClass.controls['sector_specialty'].value &&
            (fund.profile.fundFact.isSectorSpecific === true ||
              fund.profile.fundFact.isSpecialtyStockFund ===true ||
              fund.profile?.category === 'Long-Short')),
      )
    }
  }

  /*eslint complexity: [2, 10] */
  private concatParams(assetClassArr) {
    const assetClassSubFilter = []
    assetClassArr.forEach((assetClass) => {
      this.concatParamCredit(assetClass, assetClassSubFilter)
      if (
        this.activeFilters.assetClassMarketCap[assetClass] &&
        MARKET_CAPITALIZATION_FILTERS.filter(
          (value) =>
            this.activeFilters.assetClassMarketCap[assetClass].controls[value]
              .value,
        ).length > 0
      ) {
        this.numAppliedFilters += MARKET_CAPITALIZATION_FILTERS.filter(
          (value) =>
            this.activeFilters.assetClassMarketCap[assetClass].controls[value]
              .value,
        ).length
        const filteredVal = MARKET_CAPITALIZATION_FILTERS.filter(
          (value) =>
            this.activeFilters.assetClassMarketCap[assetClass].controls[value]
              .value,
        )

        for (const value of filteredVal) {
          assetClassSubFilter.push(`${assetClass}-market_cap-${value}`)
        }
      }
      if (
        this.activeFilters.assetClassStyle[assetClass] &&
        STYLE_FILTERS.filter(
          (value) =>
            this.activeFilters.assetClassStyle[assetClass].controls[value]
              .value,
        ).length > 0
      ) {
        this.numAppliedFilters += STYLE_FILTERS.filter(
          (value) =>
            this.activeFilters.assetClassStyle[assetClass].controls[value]
              .value,
        ).length
        const filteredStyleVal = STYLE_FILTERS.filter(
          (value) =>
            this.activeFilters.assetClassStyle[assetClass].controls[value]
              .value,
        )
        for (const value of filteredStyleVal) {
          assetClassSubFilter.push(`${assetClass}-style-${value}`)
        }
      } else {
        assetClassSubFilter.push(assetClass)
      }
    })
    return assetClassSubFilter
  }

  concatParamCredit(assetClass, assetClassSubFilter) {
    if (
      this.activeFilters.assetClassCredit[assetClass] &&
      CREDIT_QUALITY_FILTERS.filter(
        (value) =>
          this.activeFilters.assetClassCredit[assetClass].controls[value].value,
      ).length > 0
    ) {
      this.numAppliedFilters += CREDIT_QUALITY_FILTERS.filter(
        (value) =>
          this.activeFilters.assetClassCredit[assetClass].controls[value].value,
      ).length
      const filteredCreditVal = CREDIT_QUALITY_FILTERS.filter(
        (value) =>
          this.activeFilters.assetClassCredit[assetClass].controls[value].value,
      )

      for (const value of filteredCreditVal) {
        assetClassSubFilter.push(`${assetClass}-credit_quality-${value}`)
      }
    }
    if (
      this.activeFilters.assetClassMaturity[assetClass] &&
      MATURITY_FILTERS.filter(
        (value) =>
          this.activeFilters.assetClassMaturity[assetClass].controls[value]
            .value,
      ).length > 0
    ) {
      this.numAppliedFilters += MATURITY_FILTERS.filter(
        (value) =>
          this.activeFilters.assetClassMaturity[assetClass].controls[value]
            .value,
      ).length
      const filteredStyleVal = MATURITY_FILTERS.filter(
        (value) =>
          this.activeFilters.assetClassMaturity[assetClass].controls[value]
            .value,
      )
      for (const value of filteredStyleVal) {
        assetClassSubFilter.push(`${assetClass}-maturity-${value}`)
      }
    }
  }

  filterByStyle(assetClass, fund) {
    const isValueSelected = this.activeFilters.assetClassStyle[assetClass]
      .controls['value'].value
    const isBlendSelected = this.activeFilters.assetClassStyle[assetClass]
      .controls['blend'].value
    const isGrowthSelected = this.activeFilters.assetClassStyle[assetClass]
      .controls['growth'].value

    if (!isValueSelected && !isBlendSelected && !isGrowthSelected) {
      return true
    } else {
      if (
        isValueSelected &&
        this.checkMarketCapStyleFilter(fund, {
          styleId1: ONES,
          styleId2: FOUR,
          styleId3: SEVEN,
          type: 'Value',
        })
      ) {
        return true
      }
      if (
        isBlendSelected &&
        this.checkMarketCapStyleFilter(fund, {
          styleId1: TWO,
          styleId2: FIVE,
          styleId3: EIGHT,
          type: 'Blend',
        })
      ) {
        return true
      }
      if (
        isGrowthSelected &&
        this.checkMarketCapStyleFilter(fund, {
          styleId1: THREE,
          styleId2: SIX,
          styleId3: NINE,
          type: 'Growth',
        })
      ) {
        return true
      }
      return false
    }
  }
  // eslint-disable-next-line complexity
  filterByMarketCap(assetClass, fund) {
    const isLargeSelected = this.activeFilters.assetClassMarketCap[assetClass]
      .controls['large-cap'].value
    const isMidSelected = this.activeFilters.assetClassMarketCap[assetClass]
      .controls['mid-cap'].value
    const isSmallSelected = this.activeFilters.assetClassMarketCap[assetClass]
      .controls['small-cap'].value

    if (!isLargeSelected && !isMidSelected && !isSmallSelected) {
      return true
    } else {
      if (
        isLargeSelected &&
        this.checkMarketCapStyleFilter(fund, {
          styleId1: ONES,
          styleId2: TWO,
          styleId3: THREE,
          type: 'Large',
        })
      ) {
        return true
      }
      if (
        isMidSelected &&
        this.checkMarketCapStyleFilter(fund, {
          styleId1: FOUR,
          styleId2: FIVE,
          styleId3: SIX,
          type: 'Mid',
        })
      ) {
        return true
      }
      if (
        isSmallSelected &&
        this.checkMarketCapStyleFilter(fund, {
          styleId1: SEVEN,
          styleId2: EIGHT,
          styleId3: NINE,
          type: 'Small',
        })
      ) {
        return true
      }
      return false
    }
  }
  checkMarketCapStyleFilter(fund, ids) {
    if (
      fund.profile.equityInvestmentStyleId === ids?.styleId1 ||
      fund.profile.equityInvestmentStyleId === ids?.styleId2 ||
      fund.profile.equityInvestmentStyleId === ids?.styleId3 ||
      fund.profile.category.includes(ids?.type)
    ) {
      return true
    } else {
      return false
    }
  }

  filterByCredit(assetClass, fund) {
    const isHighSelected = this.activeFilters.assetClassCredit[assetClass]
      .controls['high'].value
    const isMediumSelected = this.activeFilters.assetClassCredit[assetClass]
      .controls['medium'].value
    const isLowSelected = this.activeFilters.assetClassCredit[assetClass]
      .controls['low'].value
    if (!isHighSelected && !isMediumSelected && !isLowSelected) {
      return true
    } else {
      if (isHighSelected && this.assignCreditMaturity(fund, ONES, TWO, THREE)) {
        return true
      }
      if (
        isMediumSelected &&
        this.assignCreditMaturity(fund, FOUR, FIVE, SIX)
      ) {
        return true
      }
      if (
        isLowSelected &&
        this.assignCreditMaturity(fund, SEVEN, EIGHT, NINE)
      ) {
        return true
      }
      return false
    }
  }
  filterByMaturity(assetClass, fund) {
    const isShortSelected = this.activeFilters.assetClassMaturity[assetClass]
      .controls['short'].value
    const isMediumSelected = this.activeFilters.assetClassMaturity[assetClass]
      .controls['medium'].value
    const isLongSelected = this.activeFilters.assetClassMaturity[assetClass]
      .controls['long'].value

    if (!isShortSelected && !isMediumSelected && !isLongSelected) {
      return true
    } else {
      if (
        isShortSelected &&
        this.assignCreditMaturity(fund, ONES, FOUR, SEVEN)
      ) {
        return true
      }
      if (
        isMediumSelected &&
        this.assignCreditMaturity(fund, TWO, FIVE, EIGHT)
      ) {
        return true
      }
      if (isLongSelected && this.assignCreditMaturity(fund, THREE, SIX, NINE)) {
        return true
      }
      return false
    }
  }
  assignCreditMaturity(fund, styleId1, styleId2, styleId3) {
    if (
      fund.profile.fixedIncomeInvestmentStyleId === styleId1 ||
      fund.profile.fixedIncomeInvestmentStyleId === styleId2 ||
      fund.profile.fixedIncomeInvestmentStyleId === styleId3
    ) {
      return true
    } else {
      return false
    }
  }
  createManagementStyleCondition(conditions, params) {
    if (
      MANAGEMENT_STYLE_FILTERS.filter(
        (value) => this.activeFilters.managementStyle.controls[value].value,
      ).length > 0 && this.activeFilters.discoverBy !== NON_VANGUARD_MF
    ) {
      this.numAppliedFilters += MANAGEMENT_STYLE_FILTERS.filter(
        (value) => this.activeFilters.managementStyle.controls[value].value,
      ).length
      params['managementstyle'] = MANAGEMENT_STYLE_FILTERS.filter(
        (value) => this.activeFilters.managementStyle.controls[value].value,
      )
        .sort()
        .toString()
        .toLowerCase()
       conditions.push(
        (fund) =>
          (this.activeFilters.managementStyle.controls['active'].value &&
          fund.profile.fundManagementStyle === "Active" ) ||
          (this.activeFilters.managementStyle.controls['index'].value &&
          fund.profile.fundManagementStyle === "Index" )
      )
    }
  }

  createShareClassCondition(conditions, params) {
    if (
      SHARE_CLASS_FILTERS.filter(
        (value) => this.activeFilters.shareClass.controls[value].value,
      ).length > 0 && this.activeFilters.discoverBy !== NON_VANGUARD_MF
    ) {
      this.numAppliedFilters += SHARE_CLASS_FILTERS.filter(
        (value) => this.activeFilters.shareClass.controls[value].value,
      ).length
      params['shareclass'] = SHARE_CLASS_FILTERS.filter(
        (value) => this.activeFilters.shareClass.controls[value].value,
      )
        .sort()
        .toString()

      conditions.push(
        // eslint-disable-next-line complexity
        (fund) =>
          (this.activeFilters.shareClass.controls['investor_shares'].value &&
            fund.profile.fundFact.isInvestorShare === true) ||
          (this.activeFilters.shareClass.controls['admiral™_shares'].value &&
            fund.profile.fundFact.isAdmiralShare === true) ||
          (this.activeFilters.shareClass.controls['institutional_shares']
            .value &&
            fund.profile.fundFact.isInstitutionalShare === true),
      )
    }
  }
  createTaxEfficiencyCondition(conditions, params) {
    if (
      TAX_EFFICIENCY_FILTERS.filter(
        (value) => this.activeFilters.taxEfficiency.controls[value].value,
      ).length > 0
    ) {
      this.numAppliedFilters += TAX_EFFICIENCY_FILTERS.filter(
        (value) => this.activeFilters.taxEfficiency.controls[value].value,
      ).length
      params['taxefficiency'] = TAX_EFFICIENCY_FILTERS.filter(
        (value) => this.activeFilters.taxEfficiency.controls[value].value,
      )
        .sort()
        .toString()

      conditions.push(
        // eslint-disable-next-line complexity
        (fund) =>
          (this.activeFilters.taxEfficiency.controls['taxable'].value &&
            fund.profile.fundFact.isTaxable === true) ||
          (this.activeFilters.taxEfficiency.controls['tax-managed'].value &&
            fund.profile.fundFact.isTaxManaged === true) ||
          (this.activeFilters.taxEfficiency.controls['tax-exempt'].value &&
            fund.profile.fundFact.isTaxExempt === true),
      )
    }
  }

  createToggleFilterCondition(params) {
    params['filters'] = this.openFilters ? "open" : "closed";
  }

  createFundFamilyCondition(params){
    if(this.FundFamilyId !== undefined  && this.activeFilters.discoverBy === NON_VANGUARD_MF){
      params['FundFamilyId'] = this.FundFamilyId
    }
  }

  createRegionCondition(conditions, params) {
    if (
      REGION_FILTERS.filter(
        (value) => this.activeFilters.region.controls[value].value,
      ).length > 0
    ) {
      this.numAppliedFilters += REGION_FILTERS.filter(
        (value) => this.activeFilters.region.controls[value].value,
      ).length;
      params['region'] = REGION_FILTERS.filter(
        (value) => this.activeFilters.region.controls[value].value,
      )
        .sort()
        .toString()
        .toLowerCase();
      conditions.push(
        (fund) =>
          (this.activeFilters.region.controls['us'].value &&
            (fund.profile.fundFact.isDomesticStockFund === true ||
              fund.profile.fundFact.isGlobal === false &&
              fund.profile.fundFact.isInternational === false)) ||
          (this.activeFilters.region.controls['international'].value &&
           (fund.profile.fundFact.isInternational === true ||
              fund.profile.fundFact.isGlobal === true ||
              fund.profile.fundFact.isInternationalStockFund === true))
      );
    }
  }

  createInvestmentMinimumCondition(conditions, params) {
    if (
      INVESTMENT_MINIMUM_FILTERS.filter(
        (value) => this.activeFilters.investmentMinimums.controls[value].value,
      ).length > 0 && this.activeFilters.discoverBy !== NON_VANGUARD_MF
    ) {
      this.numAppliedFilters += INVESTMENT_MINIMUM_FILTERS.filter(
        (value) => this.activeFilters.investmentMinimums.controls[value].value,
      ).length
      params['investmentminimumclass'] = INVESTMENT_MINIMUM_FILTERS.filter(
        (value) => this.activeFilters.investmentMinimums.controls[value].value,
      ).toString()

      conditions.push(
        // eslint-disable-next-line complexity
        (fund) =>
          (this.activeFilters.investmentMinimums.controls['1-1000'].value &&
            Number(this.getMinimum(fund)) >= ONE &&
            Number(this.getMinimum(fund)) < ONE_THOUSAND) ||
          (this.activeFilters.investmentMinimums.controls['1000-3000'].value &&
            Number(this.getMinimum(fund)) >= ONE_THOUSAND &&
            Number(this.getMinimum(fund)) < THREE_THOUSAND) ||
          (this.activeFilters.investmentMinimums.controls['3000-10000'].value &&
            Number(this.getMinimum(fund)) >= THREE_THOUSAND &&
            Number(this.getMinimum(fund)) < TEN_THOUSAND) ||
          (this.activeFilters.investmentMinimums.controls['10000-25000']
            .value &&
            Number(this.getMinimum(fund)) >= TEN_THOUSAND &&
            Number(this.getMinimum(fund)) < TWENTY_FIVE_THOUSAND) ||
          (this.activeFilters.investmentMinimums.controls['25000-50000']
            .value &&
            Number(this.getMinimum(fund)) >= TWENTY_FIVE_THOUSAND &&
            Number(this.getMinimum(fund)) < FIFTY_THOUSAND) ||
          (this.activeFilters.investmentMinimums.controls['50000>'].value &&
            Number(this.getMinimum(fund)) >= FIFTY_THOUSAND) ||
          (this.activeFilters.investmentMinimums.controls['closed'].value &&
            fund.profile.fundFact.isClosed === true) ||
          (this.activeFilters.investmentMinimums.controls['closed'].value &&
            fund.profile.fundFact.isClosedToNewInvestors === true),
      )
    }
  }

  private getMinimum(fund) {
    if (fund.profile.isETF) {
      return '1'
    }
    return fund.minimum.general.initial
  }

  createStrategyCondition(conditions, params) {
    this.aemContentSub = this.centralContentService.aemContent$.subscribe(
      (content) => {
        this.parseAEMContent(content)
      },
    )
    const strategy = (STRATEGY_FILTERS.filter(
      (value) => this.activeFilters.strategy.controls[value].value,
    ))
    if (strategy.length > 0 && this.activeFilters.discoverBy !== 'non-vanguard-mutual-funds') {

      const sortedStrategy = strategy.sort();
      params['strategy'] = sortedStrategy.toString();
      this.numAppliedFilters += strategy.length;
      conditions.push(
        // eslint-disable-next-line complexity
        (fund) =>
          (this.activeFilters.strategy.controls['all_in_one'].value !== false &&
            fund.profile.fundFact.isTargetRetirementFund === true &&
            fund.profile.fundFact.isInstitutionalShare === false &&
            fund.profile.customizedStyle === 'Balanced') ||
          (this.activeFilters.strategy.controls['all_in_one'].value !== false &&
            this.aemStringToArray(
              this.TargetEnrollmentFundIds.contentValue,
            ).includes(fund.profile.fundId)) ||
          (this.activeFilters.strategy.controls['total_market_etfs'].value !==
            false &&
            this.aemStringToArray(
            this.CorePortfolio.contentValue,
            ).includes(fund.profile.ticker)) ||
          (this.activeFilters.strategy.controls['esg'].value !== false &&
            this.aemStringToArray(this.ESGFundIds.contentValue).includes(
              fund.profile.ticker,))||
          (this.activeFilters.strategy.controls['cash'].value !== false &&
          this.aemStringToArray(this.cashFundTickers.contentValue).includes(
            fund.profile.ticker,
          )),
      )
    }
  }

  createStrategyCondition529s(conditions,params){
    const strategy = (STRATEGY_FILTERS_529.filter(
      (value) => this.activeFilters.strategy529.controls[value].value))
      if (strategy.length > 0 && this.activeFilters.discoverBy !== 'non-vanguard-mutual-funds') {
        const sortedStrategy = strategy.sort();
        params['strategy529'] = sortedStrategy.toString();
        this.numAppliedFilters += strategy.length;
        conditions.push(
          // eslint-disable-next-line complexity
          (fund) =>
            (this.activeFilters.strategy529.controls['target_enrollment'].value !== false &&
              fund.profile.is529 &&
              fund.profile.longName.includes("Target Enrollment")) ||
            (this.activeFilters.strategy529.controls['individual_balanced'].value !== false &&
              fund.profile.is529 &&
              fund.profile.fundFact.isBalanced  &&
              !fund.profile.longName.includes("Target Enrollment")
            ) ||
            (this.activeFilters.strategy529.controls['individual'].value !== false &&
              fund.profile.is529 &&
              !fund.profile.longName.includes("Target Enrollment") &&
              !fund.profile.fundFact.isBalanced
            )

        )
      }
  }

  applyChangesToUrlParams(params){
    this.activatedRoute.queryParams.subscribe((inputParams)=>{
      let currentParams = {...inputParams, ...params}
      this.router.navigate([this.activeFilters.discoverBy], {
        relativeTo: this.activatedRoute,
        queryParams: currentParams,
        replaceUrl: true,
      })
    })
  }
  
  aemStringToArray(str: string) {
    return str
      ?.replace('<p>', '')
      ?.replace('</p>', '')
      ?.replace('\n', '')
      ?.replace(/['\s\[\]]/g, '')
      ?.split(',')
  }
  ngOnDestroy(): void {
    this.aemContentSub.unsubscribe()
  }
}
